import { useState } from 'react';
import type { PublicConfig } from '../lib/api';
import { authClient, authErrorMessage, siteUrl, withNext } from '../lib/auth-client';
import { Alert, Button, Divider } from './ui';

const LABELS = { google: 'Google', discord: 'Discord' } as const;

/** "Continue with Google / Discord", shown only for providers the API has switched on. */
export function SocialButtons({
  providers,
  next = null,
}: {
  providers: PublicConfig['socialProviders'];
  /** Where to come back to afterwards, such as an invite link. */
  next?: string | null;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!providers.length) return null;

  async function continueWith(provider: keyof typeof LABELS) {
    setBusy(provider);
    setError(null);
    const { error: failed } = await authClient.signIn.social({
      provider,
      callbackURL: siteUrl(next ?? '/account'),
      // New social accounts still need a birth month before they can be used.
      newUserCallbackURL: siteUrl(withNext('/complete-profile', next)),
      errorCallbackURL: siteUrl(withNext('/sign-in', next)),
    });
    // On success the browser is already leaving for the provider.
    if (failed) {
      setError(authErrorMessage(failed));
      setBusy(null);
    }
  }

  return (
    <>
      <div className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {providers.map((provider) => (
          <Button
            key={provider}
            type="button"
            variant="secondary"
            busy={busy === provider}
            disabled={busy !== null}
            onClick={() => continueWith(provider)}
          >
            Continue with {LABELS[provider]}
          </Button>
        ))}
        <p className="text-center text-xs text-muted">
          New here? We pick a username for you. Your{' '}
          {providers.map((provider) => LABELS[provider]).join(' or ')} name and photo are never
          used.
        </p>
      </div>
      <Divider label="or with email" />
    </>
  );
}
